import React from "react";
import Div from "@jumbo/shared/Div";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Paper,
  Divider,
  Typography,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  countryFormattingRules,
  PHONE_COUNTRIES,
} from "../../../utils/constants/appActions";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GoogleIcon from "@mui/icons-material/Google";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";

const StepParent = ({
  handleBack,
  errMsg,
  values,
  setValues,
  setStep,
  isLoading,
  StepIndicator,
}) => {
  const { t } = useTranslation();

  const handleChange = (prop) => (event) => {
    let value = event.target.value;

    // Remove leading zeros from the phone number
    if (prop === "phone") {
      value = value.replace(/^0+/, "");
    }

    const selectedCountry = values.country.code;
    const formattingRule = countryFormattingRules.find(
      (rule) => rule.code === selectedCountry,
    );

    if (formattingRule) {
      const matches = value.match(formattingRule.pattern);

      if (matches) {
        // Remove undefined and join the matched groups with the specified format
        value = matches
          .slice(1)
          .filter((match) => match !== undefined)
          .join(" ");
      }
    }

    setValues({ ...values, [prop]: value });
  };

  const handleChangePhone = (event) => {
    let inputNumber = event.target.value;

    const sanitizedNumber = inputNumber.replace(/\D/g, "");

    let phone = sanitizedNumber;

    // Ensure the first digit is not 0
    phone = phone.replace(/^0+/, "");

    // Get selected country code
    const selectedCountryCode = values.country.code;

    // Find the formatting rule for the selected country code
    const countryFormattingRule = countryFormattingRules.find(
      (rule) => rule.code === selectedCountryCode,
    );

    // Enforce maximum digits restriction based on country code
    if (countryFormattingRule) {
      const maxDigits = countryFormattingRule.maxDigits;

      // If the input length exceeds the maximum allowed digits, truncate the input
      if (phone.length > maxDigits) {
        phone = phone.slice(0, maxDigits);
      }

      if (phone.length === maxDigits) {
        event.preventDefault();
      }
    }

    setValues({ ...values, phone });
  };

  const handleCountryChange = (event, newValue) => {
    console.log(newValue);
    setValues({ ...values, country: newValue });
  };

  const handleSubsribeChange = (event) => {
    setValues({ ...values, subscribeNewsLetter: event.target.checked });
  };

  const handleTermChange = (event) => {
    setValues({ ...values, terms: event.target.checked });
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 3,
          bgcolor: "#fff",
          color: "#000",
          maxWidth: 600,
          mx: "auto",
        }}
      >
        {!handleBack && (
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <img src="/images/logos/logo.png" alt="Logo" width="100" />
          </Box>
        )}

        {!handleBack && (
          <Divider sx={{ my: 2 }}>
            <Typography
              variant="h3"
              sx={{
                mb: 1,
                fontFamily: "'Red Hat Display', sans-serif",
                color: "#002465",
              }}
              align="center"
              gutterBottom
            >
              {t("parent-signup.title.signupHeadingNew")}
            </Typography>
          </Divider>
        )}

        {/*<Box sx={{ display: "flex", alignItems: "center", mb: 6 }}>
          {handleBack && (
            <IconButton onClick={handleBack} sx={{ p: 0, mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          {handleBack && (
            <Typography
              sx={{
                color: "#003366",
                fontWeight: "bold",
                fontSize: "1.15rem",
              }}
            >
              {t("parent-signup.title.signupHeadingNew")}
            </Typography>
          )}
        </Box>*/}

        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 6,
          }}
        >
          {handleBack && (
            <IconButton
              onClick={handleBack}
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                width: 40,
                height: 40,
                borderRadius: "50%", // 👈 makes it round
                border: "1px solid #002465", // 👈 outer circle border
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  bgcolor: "#f0f4f8", // light hover background
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {handleBack && (
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Red Hat Display', sans-serif",
                color: "#002465",
              }}
              align="center"
              gutterBottom
            >
              {t("parent-signup.title.signupHeadingNew")}
            </Typography>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              margin="none"
              sx={{
                mb: { xs: 1, md: 2 },
                "& .MuiInputBase-input": {
                  fontFamily: "'Lexend', sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'Lexend', sans-serif", // label font too
                },
              }}
              required
              id="firstName"
              label={t("common.firstName")}
              value={values.firstName}
              onChange={handleChange("firstName")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              margin="none"
              sx={{
                mb: { xs: 1, md: 2 },
                "& .MuiInputBase-input": {
                  fontFamily: "'Lexend', sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'Lexend', sans-serif", // label font too
                },
              }}
              required
              id="lastName"
              label={t("common.lastName")}
              value={values.lastName}
              onChange={handleChange("lastName")}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              margin="none"
              sx={{
                mb: { xs: 1, md: 2 },
                mt: { xs: 2, md: 1 },
                "& .MuiInputBase-input": {
                  fontFamily: "'Lexend', sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'Lexend', sans-serif",
                },
              }}
              required
              id="email"
              label={t("common.email")}
              InputLabelProps={{ shrink: true }}
              value={values.email}
              onChange={handleChange("email")}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                mt: { xs: 2, md: 1 },
                "& .MuiInputBase-input": {
                  fontFamily: "'Lexend', sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'Lexend', sans-serif",
                },
              }}
            >
              <InputLabel shrink htmlFor="outlined-adornment-password">
                {t("common.password")}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                type={values.showPassword ? "text" : "password"}
                value={values.password}
                onChange={handleChange("password")}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {values.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("common.password")}
                required
                sx={{ bgcolor: "#fff", borderRadius: 1 }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 1, mb: 2 }}>
          {/* Country Select */}
          <Grid item xs="auto">
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 130,
                mr: 2,
                "& .MuiInputBase-input": {
                  fontFamily: "'Lexend', sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'Lexend', sans-serif",
                },
              }}
            >
              <Autocomplete
                defaultValue={values.country}
                id="country-select-demo"
                options={PHONE_COUNTRIES}
                autoHighlight
                onChange={handleCountryChange}
                getOptionLabel={(option) => " +" + option.code}
                disableClearable
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    sx={{
                      "& > img": { mr: 1.5, flexShrink: 0 },
                      fontFamily: "'Lexend', sans-serif",
                    }}
                    {...props}
                  >
                    <img
                      loading="lazy"
                      width="20"
                      src={`https://flagcdn.com/w20/${option.countryCode.toLowerCase()}.png`}
                      srcSet={`https://flagcdn.com/w40/${option.countryCode.toLowerCase()}.png 2x`}
                      alt=""
                    />
                    {option.label} ({option.countryCode}) +{option.code}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("parent-signup.title.country")}
                    required
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        fontFamily: "'Lexend', sans-serif",
                      },
                      "& .MuiInputLabel-root": {
                        fontFamily: "'Lexend', sans-serif",
                      },
                    }}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: values.country && (
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${values.country.countryCode.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${values.country.countryCode.toLowerCase()}.png 2x`}
                          alt=""
                          style={{ marginRight: 8 }}
                        />
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          {/* Phone Input */}
          <Grid item xs>
            <TextField
              required
              id="phone"
              label={t("parent-signup.title.phone")}
              value={values.phone}
              onChange={handleChangePhone}
              fullWidth
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "'Lexend', sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "'Lexend', sans-serif",
                },
              }}
            />
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Checkbox
              checked={values.terms}
              onChange={handleTermChange}
              size="small"
              sx={{
                color: "gray",
                "&.Mui-checked": {
                  color: "#2d6bbf",
                },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                lineHeight: 1.4,
                fontFamily: "'Lexend', sans-serif",
              }}
            >
              {t("parent-signup.title.text1New")}{" "}
              <a
                href="https://hipekids.com/conditions-generales-dutilisation/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  color: "#2d6bbf",
                  fontFamily: "'Lexend', sans-serif",
                }}
              >
                {t("parent-signup.title.text2")}
              </a>{" "}
              {t("parent-signup.title.text3")}{" "}
              <a
                href="https://hipekids.com/politique-de-confidentialite/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "underline",
                  color: "#2d6bbf",
                  fontFamily: "'Lexend', sans-serif",
                }}
              >
                {t("parent-signup.title.text4")}
              </a>{" "}
              {t("parent-signup.title.text5New")}
            </Typography>
          }
          sx={{ mt: 1, mb: 2 }}
        />

        <Box
          sx={{
            width: "100%", // full width of the form container
            minHeight: 50,
            mb: 2,
            mt: 1,
          }}
        >
          {errMsg && (
            <Alert
              severity="error"
              sx={{
                height: "100%",
                fontSize: 13,
                fontFamily: "'Lexend', sans-serif",
              }}
            >
              {errMsg}
            </Alert>
          )}
        </Box>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<BubbleChartIcon />}
          sx={{
            mb: 2,
            borderRadius: 3,
            textTransform: "none",
            backgroundColor: "#1D6ADE",
            fontFamily: "'Lexend', sans-serif",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#265ca6", // darker red on hover
            },
          }}
          onClick={setStep}
          disabled={isLoading}
        >
          {t("parent-signup.title.signupNew")}
        </Button>

        {/*<Button
                variant="contained"
                fullWidth
                sx={{
                    mt: 2,
                    borderRadius: 5,
                    bgcolor: "#4A1FB8",
                    textTransform: "none",
                    py: 1.5,
                }}
            >
                {t('parent-signup.title.signupNew')}
            </Button>*/}

        <Typography
          variant="caption"
          sx={{ mt: 2, display: "block", textAlign: "center" }}
        >
          {t("parent-signup.title.alreadyMsg")}{" "}
          <a
            href="/parent/login"
            style={{
              color: "#1D6ADE",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t("parent-signup.title.signIn")}
          </a>
        </Typography>
      </Paper>
    </>
  );
};

export default StepParent;
